import React, { useState } from 'react'

import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { Markdown } from '@react-email/components'

import TextField from 'src/components/ui/text-field'
import { IPendingApproval, useAssistantAction } from 'src/hooks/useAssistant'

import { MarkdownStyles } from './markdownStyles'

interface IPlanApprovalCardProps {
  threadId: string
  pendingApproval: IPendingApproval
}

/**
 * Human-in-the-Loop card shown under an assistant message while the agents
 * service waits for the user to approve or decline a creation plan.
 * Approve resumes the run at once; decline asks what to change first.
 */
export function PlanApprovalCard({
  threadId,
  pendingApproval,
}: IPlanApprovalCardProps) {
  const resumePlan = useAssistantAction.resumePlan()
  const { isPending } = useAssistantAction.resumePlan.status()
  const [declining, setDeclining] = useState(false)
  const [comment, setComment] = useState('')
  const { payload } = pendingApproval

  const handleApprove = () => resumePlan({ threadId, approved: true })
  const handleDecline = () =>
    resumePlan({ threadId, approved: false, comment: comment.trim() })

  return (
    <Stack
      spacing={1.5}
      sx={{
        mt: 1,
        p: 1.5,
        borderRadius: '8px',
        border: '1px solid',
        borderColor: 'rgba(36, 50, 61, 0.12)',
        backgroundColor: 'rgba(11, 214, 141, 0.05)',
      }}
    >
      <Typography variant="body2" component="div">
        <Markdown
          children={payload.preview_markdown || ''}
          markdownCustomStyles={MarkdownStyles}
        />
      </Typography>
      <Typography variant="body2" fontWeight={500}>
        {payload.question}
      </Typography>
      {declining ? (
        <Stack spacing={1}>
          <TextField
            label="Что изменить?"
            value={comment}
            multiline
            fullWidth
            size="small"
            minRows={2}
            maxRows={4}
            autoFocus
            disabled={isPending}
            onFocus={(event) => event.stopPropagation()}
            onChange={(event) => setComment(event.target.value)}
          />
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              size="small"
              variant="contained"
              disabled={isPending}
              onClick={handleDecline}
            >
              Отправить
            </Button>
            <Button
              size="small"
              variant="text"
              color="secondary"
              disabled={isPending}
              onClick={() => setDeclining(false)}
            >
              Назад
            </Button>
            {isPending && <CircularProgress size={16} />}
          </Stack>
        </Stack>
      ) : (
        <Stack direction="row" spacing={1} alignItems="center">
          <Button
            size="small"
            variant="contained"
            disabled={isPending}
            onClick={handleApprove}
          >
            {payload.actions.approve}
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="secondary"
            disabled={isPending}
            onClick={() => setDeclining(true)}
          >
            {payload.actions.decline}
          </Button>
          {isPending && <CircularProgress size={16} />}
        </Stack>
      )}
    </Stack>
  )
}
